import { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { 
  Send, 
  Mail, 
  MessageSquare,
  ArrowLeft,
  ChevronDown,
  ExternalLink
} from "lucide-react";

// App Logo URL
const APP_LOGO = "https://customer-assets.emergentagent.com/job_trend-stock-app-1/artifacts/tmh7qekk_ChatGPT%20Image%20Feb%2025%2C%202026%2C%2001_23_15%20PM.png";

// Contact Email
const CONTACT_EMAIL = "support@stocktrendfusion.app";

// FAQ Data
const faqItems = [
  {
    question: "How do I create an account?",
    answer: "Download StockTrendFusion from the App Store and follow the simple registration process. You can sign up with your email or Apple ID for quick access."
  },
  {
    question: "Is my financial data secure?",
    answer: "Absolutely. We use bank-level 256-bit AES encryption to protect all your data. Your financial information is never shared with third parties and is stored securely on encrypted servers."
  },
  {
    question: "What markets does StockTrendFusion cover?",
    answer: "We cover major US stock exchanges (NYSE, NASDAQ), international markets, ETFs, mutual funds, and cryptocurrencies. Real-time data is available for most major exchanges."
  },
  {
    question: "How do the astrological cycles work?",
    answer: "Our platform converts planetary transits, lunar phases, and other astrological markers into quantifiable features. These are integrated into our scoring system as experimental timing indicators. Please note these are for research purposes only."
  },
  {
    question: "How do I cancel my subscription?",
    answer: "You can manage or cancel your subscription through your Apple ID settings. Go to Settings > Apple ID > Subscriptions on your iOS device to make changes."
  }
];

// Navbar Component
const Navbar = () => (
  <nav className="fixed top-0 left-0 right-0 z-50 bg-[#030712]/80 backdrop-blur-xl border-b border-white/5">
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <div className="flex items-center justify-between h-16">
        <Link to="/" className="flex items-center gap-3" data-testid="nav-logo">
          <img src={APP_LOGO} alt="StockTrendFusion" className="w-10 h-10 rounded-xl" />
          <span className="font-semibold text-lg text-white">StockTrendFusion</span>
        </Link>
        <Link 
          to="/" 
          className="flex items-center gap-2 text-sm text-zinc-400 hover:text-white transition-colors"
          data-testid="nav-back-link"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Home
        </Link>
      </div>
    </div>
  </nav>
);

// FAQ Accordion Item
const FAQItem = ({ question, answer, isOpen, onClick, index }) => (
  <div className="faq-item py-6" data-testid={`faq-item-${index}`}>
    <button
      onClick={onClick}
      className="w-full flex items-center justify-between text-left"
      data-testid={`faq-toggle-${index}`}
    >
      <span className="text-lg font-medium text-white pr-4">{question}</span>
      <ChevronDown 
        className={`w-5 h-5 text-zinc-400 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}
      />
    </button>
    <motion.div
      initial={false}
      animate={{ height: isOpen ? 'auto' : 0, opacity: isOpen ? 1 : 0 }}
      transition={{ duration: 0.3 }}
      className="overflow-hidden"
    >
      <p className="pt-4 text-zinc-400 leading-relaxed">{answer}</p>
    </motion.div>
  </div>
);

// Contact Card Component
const ContactCard = () => {
  return (
    <div className="text-center py-8" data-testid="contact-card">
      <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-cyan-500 to-blue-500 flex items-center justify-center mx-auto mb-6 shadow-lg shadow-cyan-500/25">
        <Mail className="w-10 h-10 text-white" strokeWidth={1.5} />
      </div>
      <h3 className="text-2xl font-semibold text-white mb-3">Get in Touch</h3>
      <p className="text-zinc-400 mb-8 max-w-md mx-auto">
        Have questions or feedback? We'd love to hear from you. Send us an email and we'll get back to you within 24-48 hours.
      </p>
      <a
        href={`mailto:${CONTACT_EMAIL}?subject=StockTrendFusion Support`}
        className="inline-flex items-center gap-3 px-8 py-4 rounded-full bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-400 hover:to-blue-400 text-white font-medium transition-all btn-lift shadow-lg shadow-cyan-500/25"
        data-testid="contact-email-btn"
      >
        <Send className="w-5 h-5" />
        Email Us
        <ExternalLink className="w-4 h-4" />
      </a>
      <p className="mt-4 text-sm text-zinc-500">
        {CONTACT_EMAIL}
      </p>
    </div>
  );
};

// Footer
const Footer = () => (
  <footer className="py-12 border-t border-white/5" data-testid="footer">
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <div className="flex flex-col md:flex-row items-center justify-between gap-6">
        <div className="flex items-center gap-3">
          <img src={APP_LOGO} alt="StockTrendFusion" className="w-10 h-10 rounded-xl" />
          <span className="font-semibold text-white">StockTrendFusion</span>
        </div>
        
        <div className="flex items-center gap-8">
          <Link 
            to="/support" 
            className="text-sm text-zinc-400 hover:text-white transition-colors"
            data-testid="footer-support-link"
          >
            Support
          </Link>
          <Link 
            to="/privacy" 
            className="text-sm text-zinc-400 hover:text-white transition-colors"
            data-testid="footer-privacy-link"
          >
            Privacy Policy
          </Link>
          <Link 
            to="/terms" 
            className="text-sm text-zinc-400 hover:text-white transition-colors"
            data-testid="footer-terms-link"
          >
            Terms of Service
          </Link>
        </div>
        
        <p className="text-sm text-zinc-500">
          &copy; {new Date().getFullYear()} StockTrendFusion. All rights reserved.
        </p>
      </div>
    </div>
  </footer>
);

// Main Support Page Component
const SupportPage = () => {
  const [openFAQ, setOpenFAQ] = useState(null);

  return (
    <div className="min-h-screen" data-testid="support-page">
      <Navbar />
      
      {/* Header */}
      <section className="pt-32 pb-16 relative">
        <div className="absolute inset-0 grid-pattern opacity-30" />
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <motion.div 
            className="text-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <p className="text-sm font-medium tracking-wider uppercase text-cyan-400 mb-4">
              Help Center
            </p>
            <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-white mb-4">
              How Can We Help?
            </h1>
            <p className="text-lg text-zinc-400 max-w-2xl mx-auto">
              Get in touch with our support team or browse our FAQ section for quick answers.
            </p>
          </motion.div>
        </div>
      </section>
      
      <div className="max-w-7xl mx-auto px-6 md:px-12 pb-24">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
          {/* Contact Card */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <div className="rounded-2xl border border-white/5 bg-white/5 backdrop-blur-sm p-8">
              <ContactCard />
            </div>
          </motion.div>
          
          {/* FAQ Section */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <div className="rounded-2xl border border-white/5 bg-white/5 backdrop-blur-sm p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl bg-purple-500/10 flex items-center justify-center">
                  <MessageSquare className="w-5 h-5 text-purple-400" strokeWidth={1.5} />
                </div>
                <h2 className="text-2xl font-semibold text-white">FAQ</h2>
              </div>
              
              <div className="divide-y divide-white/10" data-testid="faq-section">
                {faqItems.map((item, index) => (
                  <FAQItem
                    key={index}
                    index={index}
                    question={item.question}
                    answer={item.answer}
                    isOpen={openFAQ === index}
                    onClick={() => setOpenFAQ(openFAQ === index ? null : index)}
                  />
                ))}
              </div>
            </div>
          </motion.div>
        </div>
        
        {/* Quick Links */}
        <motion.div 
          className="mt-16 text-center"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
        >
          <p className="text-zinc-400 mb-4">Need more information?</p>
          <div className="flex items-center justify-center gap-6">
            <Link 
              to="/privacy" 
              className="text-cyan-400 hover:text-cyan-300 transition-colors underline"
              data-testid="quick-privacy-link"
            >
              Privacy Policy
            </Link>
            <span className="text-zinc-600">|</span>
            <Link 
              to="/terms" 
              className="text-cyan-400 hover:text-cyan-300 transition-colors underline"
              data-testid="quick-terms-link"
            >
              Terms of Service
            </Link>
          </div>
        </motion.div>
      </div>
      
      <Footer />
    </div>
  );
};

export default SupportPage;
